import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaSearch, FaDownload, FaUser, FaIdCard, FaCalendar, FaFile } from "react-icons/fa";

import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import { catchError, changeState, enterFn } from "JS/common";
import { downloadPrivacyFile, getFileList } from "JS/API";

// 디자인 시스템 임포트
import PageContainer from "Components/UI/PageContainer";
import SearchCard from "Components/UI/SearchCard";
import FormField from "Components/UI/FormField";
import Input from "Components/UI/Input";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";
import Badge from "Components/UI/Badge";
import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { commonStyles, designTokens, listPageStyles } from "utils/designSystem";

const UploadFilePage = () => {
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [list, setList] = useState([]);
  const [modal] = useState(false);
  const [searchTxt, setSearchTxt] = useState("");
  let prevent = false;

  // 추가 스타일
  const pageStyles = {
    searchSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      flexWrap: 'wrap'
    },
    searchGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      flex: 1,
      minWidth: '300px'
    },
    controlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md
    },
    downloadButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
      fontSize: '12px'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      fontWeight: '500'
    },
    fileInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    noDataContainer: listPageStyles.noDataContainer};

  // 파일 타입 매핑
  const fileTypeLabelMap = {
    biz_license: "사업자 등록증",
    stamp: "인감",
    stamp_certificate: "인감 증명서",
    etc: "기타 서류",
  };

  // 파일 타입별 배지 variant
  const getFileTypeBadgeVariant = (fileType) => {
    const variants = {
      biz_license: 'primary',
      stamp: 'success',
      stamp_certificate: 'warning',
      etc: 'neutral'
    };
    return variants[fileType] || 'neutral';
  };

  const getPaymentsHistory = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);

    const result = await getFileList(pageInfo.page, pageInfo.limit, searchTxt);
    if (typeof result === "object") {
      setList(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const userSearch = async () => {
    const result = await getFileList(pageInfo.page, pageInfo.limit, searchTxt);
    if (typeof result === "object") {
      setList(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const handleSearchReset = () => {
    setSearchTxt("");
    setPageInfo(prev => ({ ...prev, page: 1 }));
  };

  //= 파일 목록 테이블 렌더
  const renderTableBody = () => {
    return list.map(({
      id,
      user_id,
      original_file_name,
      file_type,
      uploaded_at,
      file_name,
      user_name,
    }) => {
      const handleDownload = () => {
        downloadPrivacyFile(file_name, original_file_name);
      };

      return (
        <tr key={id}>
          <TableCell>
            <div style={pageStyles.userInfo}>
              <FaUser size={12} style={{ color: designTokens.colors.text.tertiary }} />
              {user_name}
            </div>
          </TableCell>

          <TableCell>
            <div style={pageStyles.userInfo}>
              <FaIdCard size={12} style={{ color: designTokens.colors.text.tertiary }} />
              {user_id}
            </div>
          </TableCell>

          <TableCell>
            <Badge variant={getFileTypeBadgeVariant(file_type)}>
              {fileTypeLabelMap[file_type] || file_type}
            </Badge>
          </TableCell>

          <TableCell>
            <div style={pageStyles.fileInfo}>
              <FaFile size={12} style={{ color: designTokens.colors.text.tertiary }} />
              <span style={{
                fontWeight: '500',
                wordBreak: 'break-all',
                lineHeight: '1.4'
              }}>
                {original_file_name}
              </span>
            </div>
          </TableCell>

          <TableCell>
            <div style={pageStyles.fileInfo}>
              <FaCalendar size={12} style={{ color: designTokens.colors.text.tertiary }} />
              {uploaded_at}
            </div>
          </TableCell>

          <TableCell>
            <Button
              variant="primary"
              icon={<FaDownload size={10} />}
              onClick={handleDownload}
              style={{
                padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
                fontSize: '12px'
              }}
            >
              다운로드
            </Button>
          </TableCell>
        </tr>
      );
    });
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 파일 관리";
  }, []);

  useEffect(() => {
    if (!modal) {
      getPaymentsHistory();
    }
  }, [pageInfo.page, pageInfo.limit, modal]);

  return (
    <PageContainer
      title="FILE LIST"
      icon={<FaFileAlt />}
    >
      {/* 검색 및 컨트롤 영역 */}
      <SearchCard>
        <div style={pageStyles.searchSection}>
          {/* 검색 그룹 */}
          <div style={pageStyles.searchGroup}>
            <FormField style={{ flex: 1, marginBottom: 0 }}>
              <Input
                type="text"
                placeholder="사용자명 또는 파일명으로 검색하세요."
                value={searchTxt}
                onChange={(e) => setSearchTxt(e.target.value)}
                onKeyDown={(e) => {
                  if (!alertBox.bool) enterFn(e, userSearch);
                }}
              />
            </FormField>

            <Button
              variant="primary"
              icon={<FaSearch />}
              onClick={userSearch}
            >
              검색
            </Button>

            {searchTxt && (
              <Button
                variant="secondary"
                onClick={handleSearchReset}
              >
                초기화
              </Button>
            )}
          </div>

          {/* 페이지 설정 */}
          <div style={pageStyles.controlGroup}>
            <Select
              value={pageInfo.limit}
              onChange={(e) => {
                setPageInfo((prev) => {
                  const clone = { ...prev };
                  clone.page = 1;
                  clone.limit = e.target.value;
                  return clone;
                });
              }}
              style={{ minWidth: '140px' }}
            >
              <option value={10}>10개씩 보기</option>
              <option value={30}>30개씩 보기</option>
              <option value={50}>50개씩 보기</option>
            </Select>
          </div>
        </div>
      </SearchCard>

      {/* 테이블 또는 빈 상태 */}
      {list?.length ? (
        <TableContainer>
          <Table>
            <colgroup>
              <col width="15%" />  
              <col width="15%" /> 
              <col width="15%" /> 
              <col width="30%" /> 
              <col width="15%" />  
              <col width="10%" /> 
            </colgroup>
            <thead>
              <tr>
                <TableHeader>이름</TableHeader>
                <TableHeader>아이디</TableHeader>
                <TableHeader>파일유형</TableHeader>
                <TableHeader>파일명</TableHeader>
                <TableHeader>업로드 일자</TableHeader>
                <TableHeader>다운로드</TableHeader>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </TableContainer>
      ) : (
        <div style={pageStyles.noDataContainer}>
          <FaFileAlt
            size={48}
            style={{
              color: designTokens.colors.text.muted,
              marginBottom: designTokens.spacing.md
            }}
          />
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: designTokens.spacing.xs }}>
            {searchTxt ? '검색 결과가 없습니다' : '파일이 없습니다'}
          </div>
          <div style={{ fontSize: '14px' }}>
            {searchTxt
              ? '다른 검색어로 시도해보세요.'
              : '업로드된 파일이 표시될 예정입니다.'
            }
          </div>
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}
    </PageContainer>
  );
};

export default UploadFilePage;